import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getUnreadCount, getNotifications, markAllNotificationsRead } from '@/services/notificationService';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import type { ApiNotification } from '@/lib/types';

import logoYonyonHeader from '@/assets/logo-yonyon-header-300x100.png'

interface NavbarProps {
  onCreateClick: () => void;
  createLabel?: string;
  createIcon?: string;
  search?: string;
  onSearchChange?: (value: string) => void;
}

interface NavLink {
  label: string;
  href?: string;
  to?: string;
  icon: string;
  badge?: string;
}

const navLinks: NavLink[] = [
  { label: 'Accueil', to: '/', icon: 'ri-home-5-line' },
  { label: 'Activités', href: '/#activites', icon: 'ri-compass-3-line' },
  // { label: 'Le Coin', to: '/le-coin', icon: 'ri-chat-smile-3-line' } Desactivé temporaire,
  { label: "S'entraider", href: '/#entraide', icon: 'ri-hand-heart-line' },
  { label: 'Carte', href: '/#carte', icon: 'ri-map-pin-2-line' },
];

export default function Navbar({ onCreateClick, createLabel = 'Publier', createIcon = 'ri-add-line', search = '', onSearchChange }: NavbarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifications, setNotifications] = useState<ApiNotification[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Poll unread count when user is logged in
  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    const fetch = () => getUnreadCount().then(setUnreadCount).catch(() => { });
    fetch();
    const interval = window.setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleRealtimeNotification = useCallback((payload: unknown) => {
    const p = payload as Record<string, unknown> | null;
    const message =
      (typeof p?.message === 'string' && p.message) ||
      (typeof p?.content === 'string' && p.content) ||
      (typeof p?.title === 'string' && p.title) ||
      'Nouvelle notification';
    setUnreadCount((c) => c + 1);
    setToast(message);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 4000);
  }, []);

  useRealtimeNotifications(!!user, handleRealtimeNotification);

  // Nettoyage du timer de toast au démontage
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const handleOpenNotif = async () => {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next) {
      try {
        const data = await getNotifications();
        setNotifications(data);
        if (unreadCount > 0) {
          await markAllNotificationsRead();
          setUnreadCount(0);
        }
      } catch {
        // ignore
      }
    }
  };

  const handleLogout = () => {
    setAccountOpen(false);
    setMobileOpen(false);
    logout();
    navigate('/');
  };

  const initials = user
    ? user.name
      .split(' ')
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
    : '';

  return (
    <>
      <header
        className="sticky top-0 z-[10000] bg-white/95 backdrop-blur border-b border-background-200 relative isolate"
      >
        <div className="px-4 md:px-8 h-[4.5rem] max-[430px]:h-[4rem] flex items-center justify-between lg:grid lg:grid-cols-3 lg:gap-4">
          {/* Brand */}
          <Link
            to="/"
            className="flex items-center cursor-pointer lg:justify-self-start"
          >
            <img
              src={logoYonyonHeader}
              alt="Yonyon"
              className="h-16 max-[430px]:h-[3.2rem] w-auto object-contain -translate-x-8"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center justify-center gap-1 lg:justify-self-center">
            {navLinks.map((link) => {
              const cls =
                'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap';
              const content = (
                <>
                  <i className={`${link.icon} text-base`}></i>
                  {link.label}
                  {link.badge && (
                    <span className="px-1.5 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-bold leading-none">
                      {link.badge}
                    </span>
                  )}
                </>
              );
              return link.to ? (
                <Link key={link.label} to={link.to} className={cls}>
                  {content}
                </Link>
              ) : (
                <a key={link.label} href={link.href} className={cls}>
                  {content}
                </a>
              );
            })}
          </nav>

          {/* Actions */}
          <div className="hidden lg:flex items-center gap-3 lg:justify-self-end">
            {user ? (
              <>
                {/* Notifications bell */}
                <div className="relative" ref={notifRef}>
                  <button
                    type="button"
                    onClick={handleOpenNotif}
                    className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer text-foreground-600"
                    aria-label="Notifications"
                  >
                    <i className="ri-notification-3-line text-lg"></i>
                    {unreadCount > 0 && (
                      <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {notifOpen && (
                    <div className="absolute right-0 mt-2 w-80 bg-white border border-background-200 rounded-lg overflow-hidden z-50 animate-scale-in">
                      <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between">
                        <p className="text-sm font-semibold text-foreground-950">Notifications</p>
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="py-8 text-center text-sm text-foreground-400">
                            <i className="ri-notification-off-line text-2xl block mb-2"></i>
                            Aucune notification
                          </div>
                        ) : (
                          notifications.map((n) => (
                            <div
                              key={n.id}
                              className={`px-4 py-3 border-b border-background-50 text-sm ${n.isRead ? 'text-foreground-500' : 'text-foreground-900 font-medium bg-primary-50/30'}`}
                            >
                              <p>{n.message}</p>
                              <p className="text-xs text-foreground-400 mt-0.5">
                                {new Date(n.createdAt).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Account dropdown */}
                <div className="relative" ref={accountRef}>
                  <button
                    type="button"
                    onClick={() => setAccountOpen((v) => !v)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold">
                        {initials}
                      </span>
                    )}
                    <span className="text-sm font-medium text-foreground-700 max-w-[120px] truncate">
                      {user.name.split(' ')[0]}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400"></i>
                  </button>

                  {accountOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-background-200 rounded-lg overflow-hidden animate-scale-in">
                      <div className="px-4 py-3 border-b border-background-100">
                        <p className="text-sm font-semibold text-foreground-950 truncate">
                          {user.name}
                        </p>
                        <p className="text-xs text-foreground-400 truncate">{user.email}</p>
                      </div>
                      <Link
                        to="/mon-profil"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 sm:py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <i className="ri-user-line text-base"></i>
                        Mon profil
                      </Link>
                      <Link
                        to="/mes-activites"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 sm:py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <i className="ri-calendar-check-line text-base"></i>
                        Mes activités
                      </Link>
                      {(user.isAdmin || user.isModerator) && (
                        <Link
                          to="/admin"
                          onClick={() => setAccountOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <i className="ri-shield-check-line text-base"></i>
                          Administration
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer text-left"
                      >
                        <i className="ri-logout-box-r-line text-base"></i>
                        Se déconnecter
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link
                to="/auth"
                className="px-4 py-2 rounded-md text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Se connecter
              </Link>
            )}

            <button
              type="button"
              onClick={onCreateClick}
              className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className={`${createIcon} text-base`}></i>
              {createLabel}
            </button>
          </div>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center">



            {/* Recherche */}
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="Rechercher"
            >
              <i className="ri-search-line text-xl"></i>
            </button>


            {/* profil name */}
            {user && (
              <Link
                to="/mon-profil"
                className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-background-100 transition-colors cursor-pointer max-w-[150px]"
                aria-label="Mon profil"
              >
                <span className="w-[1.5rem] h-[1.5rem] rounded-full bg-primary-500 text-white flex items-center justify-center text-[0.65rem] font-bold shrink-0">
                  {initials}
                </span>

                <span className="text-sm font-medium text-foreground-700 max-w-[80px] truncate">
                  {user.name.split(' ')[0]}
                </span>
              </Link>
            )}



            {/* Menu */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="Menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>

          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-background-200 bg-white px-4 py-3 animate-fade-in">
            <div className="flex flex-col gap-1">
              {user && (
                <div className="flex items-center gap-3 px-3 py-2 sm:py-3 mb-1 bg-background-50 rounded-lg">
                  <span className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold">
                    {initials}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-950 truncate">
                      {user.name}
                    </p>
                    <p className="text-xs text-foreground-400 truncate">{user.email}</p>
                  </div>
                </div>
              )}

              {navLinks.map((link) => {
                const cls =
                  'flex items-center gap-3 px-3 py-2 sm:py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer';
                const content = (
                  <>
                    <i className={`${link.icon} text-lg`}></i>
                    {link.label}
                    {link.badge && (
                      <span className="ml-auto px-1.5 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-bold leading-none">
                        {link.badge}
                      </span>
                    )}
                  </>
                );
                return link.to ? (
                  <Link
                    key={link.label}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={cls}
                  >
                    {content}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={cls}
                  >
                    {content}
                  </a>
                );
              })}

              {user && (
                <Link
                  to="/mon-profil"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 sm:py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-user-line text-lg"></i>
                  Mon profil
                </Link>
              )}

              {user && (
                <Link
                  to="/mes-activites"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 sm:py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  Mes activités
                </Link>
              )}

              <div className="flex flex-col gap-2 pt-2 mt-2 border-t border-background-200">
                {user ? (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="px-4 py-2.5 rounded-md text-sm font-semibold text-foreground-700 bg-background-100 cursor-pointer whitespace-nowrap"
                  >
                    Se déconnecter
                  </button>
                ) : (
                  <Link
                    to="/auth"
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-2.5 rounded-md text-sm font-semibold text-foreground-700 bg-background-100 text-center cursor-pointer whitespace-nowrap"
                  >
                    Se connecter
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    onCreateClick();
                  }}
                  className="px-4 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className={`${createIcon} text-base`}></i>
                  {createLabel}
                </button>
              </div>
            </div>
          </div>
        )}

        {searchOpen && (
          <div className="lg:hidden absolute top-[4.5rem] left-0 right-0 bg-white border-t border-background-200 px-4 py-3 z-50">
            <div className="flex items-center gap-2">
              <i className="ri-search-line text-foreground-400 text-lg"></i>

              <input
                type="text"
                value={search}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchOpen(false);
                  }
                }}
                placeholder="Rechercher..."
                autoFocus
                className="flex-1 min-w-0 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />

              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-500"
                aria-label="Fermer la recherche"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        )}
      </header>

      {toast && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 flex items-start gap-3 max-w-sm mx-auto sm:mx-0 px-4 py-3 bg-white border border-background-200 rounded-lg animate-fade-in-up">
          <span className="w-8 h-8 rounded-full bg-accent-500 text-white flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-notification-3-line text-base"></i>
          </span>
          <p className="text-sm text-foreground-800 leading-snug">{toast}</p>
        </div>
      )}
    </>
  );
}